import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import type { ServiceBookLink } from '@/hooks/useTenantSettings';

const MAX_SERVICE_BOOK_LINKS = 20;

export interface ServiceBookLinksSettingsProps {
  links: ServiceBookLink[];
  onChange: (links: ServiceBookLink[]) => void;
}

type UrlError = 'required' | 'protocol';

function urlErrorFor(raw: string): UrlError | null {
  const url = raw.trim();
  if (!url) return 'required';
  if (!url.startsWith('https://') && !url.startsWith('http://')) return 'protocol';
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return 'protocol';
    if (!parsed.hostname) return 'protocol';
  } catch {
    return 'protocol';
  }
  return null;
}

function toStoredLink(name: string, url: string, note: string): ServiceBookLink {
  const trimmedNote = note.trim();
  return {
    name: name.trim(),
    url: url.trim(),
    ...(trimmedNote !== '' ? { note: trimmedNote } : {}),
  };
}

export function ServiceBookLinksSettings({ links, onChange }: ServiceBookLinksSettingsProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [nameError, setNameError] = useState(false);
  const [urlError, setUrlError] = useState<UrlError | null>(null);

  const atMax = links.length >= MAX_SERVICE_BOOK_LINKS && editingIndex === null;

  const resetDraft = () => {
    setName('');
    setUrl('');
    setNote('');
    setEditingIndex(null);
    setNameError(false);
    setUrlError(null);
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const nextUrlError = urlErrorFor(url);
    const nextNameError = trimmedName.length === 0;
    setNameError(nextNameError);
    setUrlError(nextUrlError);
    if (nextNameError || nextUrlError !== null) return;
    if (trimmedName.length > 80) return;
    if (note.trim().length > 200) return;

    const entry = toStoredLink(name, url, note);
    if (editingIndex === null) {
      if (links.length >= MAX_SERVICE_BOOK_LINKS) return;
      onChange([...links, entry]);
    } else {
      const next = [...links];
      next[editingIndex] = entry;
      onChange(next);
    }
    resetDraft();
  };

  const handleEdit = (index: number) => {
    const link = links[index];
    if (!link) return;
    setName(link.name);
    setUrl(link.url);
    setNote(link.note ?? '');
    setEditingIndex(index);
    setNameError(false);
    setUrlError(null);
  };

  const handleDelete = (index: number) => {
    onChange(links.filter((_, i) => i !== index));
    if (editingIndex === index) {
      resetDraft();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('serviceBookLinks.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('serviceBookLinks.description')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        {links.length === 0 && editingIndex === null && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
            {t('serviceBookLinks.empty')}
          </Typography>
        )}

        {links.map((link, index) => (
          <Box
            key={`${link.url}-${String(index)}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {link.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {link.url}
              </Typography>
              {link.note !== undefined && link.note !== '' && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {link.note}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title={tc('edit')}>
                <IconButton size="small" onClick={() => handleEdit(index)} aria-label={tc('edit')}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={tc('delete')}>
                <IconButton size="small" onClick={() => handleDelete(index)} aria-label={tc('delete')}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            label={t('serviceBookLinks.name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? t('serviceBookLinks.nameRequired') : undefined}
            disabled={atMax}
            slotProps={{ htmlInput: { maxLength: 80 } }}
          />
          <TextField
            size="small"
            fullWidth
            label={t('serviceBookLinks.url')}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (urlError !== null) setUrlError(null);
            }}
            error={urlError !== null}
            helperText={
              urlError === 'required'
                ? t('serviceBookLinks.urlRequired')
                : urlError === 'protocol'
                  ? t('serviceBookLinks.urlProtocol')
                  : undefined
            }
            disabled={atMax}
          />
          <TextField
            size="small"
            fullWidth
            label={t('serviceBookLinks.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            helperText={t('serviceBookLinks.noteHint')}
            disabled={atMax}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={editingIndex === null ? <AddIcon /> : undefined}
              onClick={handleSubmit}
              disabled={atMax}
            >
              {editingIndex === null ? t('serviceBookLinks.add') : tc('save')}
            </Button>
            {editingIndex !== null && (
              <Button size="small" onClick={resetDraft}>
                {tc('cancel')}
              </Button>
            )}
          </Box>
          {atMax && (
            <Typography variant="caption" color="text.secondary">
              {t('serviceBookLinks.maxReached')}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
